import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Users, Pencil, Phone, MessageSquare, X, Save, Send, RefreshCw, Info, ChevronDown, ChevronUp, User } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminInstructors = () => {
  const { getAuthHeader } = useAdminAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [smsModal, setSmsModal] = useState(null);
  const [smsMessage, setSmsMessage] = useState('');
  const [sendingSms, setSendingSms] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/fitnet/instructors`, {
        headers: getAuthHeader()
      });
      setEmployees(response.data);
      
      // Auto-expand all groups initially
      const groups = {};
      response.data.forEach(emp => {
        if (emp.groupName) {
          groups[emp.groupName] = true;
        }
      });
      setExpandedGroups(groups);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
      toast.error('Nie udało się pobrać pracowników z FitNet');
    } finally {
      setLoading(false);
    }
  };

  // Group employees by groupName
  const groupedEmployees = employees.reduce((acc, emp) => {
    const group = emp.groupName || 'Bez grupy';
    if (!acc[group]) acc[group] = [];
    acc[group].push(emp);
    return acc;
  }, {});

  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  const handleSyncFromFitnet = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/api/admin/fitnet/instructors/sync`, {}, {
        headers: getAuthHeader()
      });
      toast.success(response.data.message);
      fetchEmployees();
    } catch (error) {
      toast.error('Błąd synchronizacji');
    } finally {
      setSyncing(false);
    }
  };

  const handleSaveEmployee = async () => {
    try {
      await axios.put(
        `${API}/api/admin/instructors/${editingEmployee.id}`,
        {
          phone: editingEmployee.phone,
          smsEnabled: editingEmployee.smsEnabled,
          isActive: true
        },
        { headers: getAuthHeader() }
      );
      toast.success('Pracownik zaktualizowany');
      setEditingEmployee(null);
      fetchEmployees();
    } catch (error) {
      console.error('Save employee error:', error);
      toast.error('Nie udało się zapisać');
    }
  };

  const handleSendSms = async () => {
    if (!smsMessage.trim()) {
      toast.error('Wiadomość jest wymagana');
      return;
    }
    
    setSendingSms(true);
    try {
      await axios.post(
        `${API}/api/admin/sms/send`,
        {
          instructorFitnetId: smsModal.id,
          message: smsMessage
        },
        { headers: getAuthHeader() }
      );
      toast.success('SMS wysłany');
      setSmsModal(null);
      setSmsMessage('');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Nie udało się wysłać SMS');
    } finally {
      setSendingSms(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Pracownicy</h1>
          <p className="text-gray-400">
            {employees.length} pracowników w {Object.keys(groupedEmployees).length} grupach
          </p>
        </div>
        <Button
          onClick={handleSyncFromFitnet}
          disabled={syncing}
          className="bg-purple-600 hover:bg-purple-700"
        >
          {syncing ? (
            <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Synchronizacja...</>
          ) : (
            <><RefreshCw className="w-4 h-4 mr-2" /> Synchronizuj z FitNet</>
          )}
        </Button>
      </div>

      {/* Info */}
      <Card className="bg-blue-500/10 border-blue-500/30">
        <CardContent className="p-4 flex items-center gap-3">
          <Info className="w-5 h-5 text-blue-400 flex-shrink-0" />
          <p className="text-gray-300 text-sm">
            Lista pracowników jest automatycznie pobierana z systemu FitNet, pogrupowana według działów.
            Możesz dodać numer telefonu aby włączyć powiadomienia SMS.
          </p>
        </CardContent>
      </Card>

      {/* Grouped Employees */}
      <div className="space-y-4">
        {Object.entries(groupedEmployees).map(([groupName, groupEmployees]) => (
          <Card key={groupName} className="mb-4 bg-white/5 border-white/10">
            <button
              onClick={() => toggleGroup(groupName)}
              className="w-full flex items-center justify-between p-4 text-left hover:bg-white/5 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-white font-medium">{groupName}</h3>
                  <p className="text-gray-500 text-sm">{groupEmployees.length} pracowników</p>
                </div>
              </div>
              {expandedGroups[groupName] ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>
            
            {expandedGroups[groupName] && (
              <div className="border-t border-white/10">
                <div className="grid gap-3 p-4 md:grid-cols-2 lg:grid-cols-3">
                  {groupEmployees.map((employee) => (
                    <div key={employee.id} className="bg-white/5 rounded-lg p-3">
                      <div className="flex items-start gap-3">
                        {employee.photo ? (
                          <img 
                            src={employee.photo} 
                            alt={employee.fullName} 
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                            <User className="w-6 h-6 text-purple-400" />
                          </div>
                        )}
                        
                        <div className="flex-1 min-w-0">
                          <h4 className="text-white font-medium truncate">
                            {employee.fullName || employee.name}
                          </h4>
                          <p className="text-gray-500 text-xs mt-1">
                            {employee.phone ? (
                              <span className="flex items-center gap-1 text-green-400">
                                <Phone className="w-3 h-3" />
                                {employee.phone}
                              </span>
                            ) : (
                              <span className="text-gray-600">Brak telefonu</span>
                            )}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 mt-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingEmployee(employee)}
                          className="flex-1 h-8 text-xs"
                        >
                          <Pencil className="w-3 h-3 mr-1" />
                          Edytuj
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSmsModal(employee)}
                          disabled={!employee.phone}
                          className="flex-1 h-8 text-xs"
                        >
                          <MessageSquare className="w-3 h-3 mr-1" />
                          SMS
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        ))}
      </div>

      {employees.length === 0 && (
        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-8 text-center">
            <Users className="w-12 h-12 mx-auto text-gray-500 mb-4" />
            <p className="text-gray-400 mb-4">Brak pracowników</p>
            <Button onClick={handleSyncFromFitnet} disabled={syncing}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Pobierz z FitNet
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Edit Employee Modal */}
      {editingEmployee && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <Card className="bg-gray-900 border-white/10 w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white">Edytuj Pracownika</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setEditingEmployee(null)}>
                <X className="w-5 h-5" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center p-4 bg-white/5 rounded-lg">
                {editingEmployee.photo && (
                  <img 
                    src={editingEmployee.photo} 
                    alt={editingEmployee.fullName}
                    className="w-20 h-20 rounded-lg object-cover mx-auto mb-3"
                  />
                )}
                <p className="text-white font-medium">{editingEmployee.fullName || editingEmployee.name}</p>
                <p className="text-gray-500 text-sm">FitNet ID: {editingEmployee.id}</p>
                {editingEmployee.groupName && (
                  <p className="text-purple-400 text-sm">{editingEmployee.groupName}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">Numer telefonu (do SMS)</Label>
                <Input
                  value={editingEmployee.phone || ''}
                  onChange={(e) => setEditingEmployee({ ...editingEmployee, phone: e.target.value })}
                  placeholder="+48 123 456 789"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>

              <div className="flex items-center gap-3">
                <Switch
                  checked={editingEmployee.smsEnabled || false}
                  onCheckedChange={(checked) => setEditingEmployee({ ...editingEmployee, smsEnabled: checked })}
                />
                <Label className="text-gray-300">Włącz powiadomienia SMS</Label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                <Button variant="outline" onClick={() => setEditingEmployee(null)}>
                  Anuluj
                </Button>
                <Button onClick={handleSaveEmployee} className="bg-purple-600 hover:bg-purple-700">
                  <Save className="w-4 h-4 mr-2" />
                  Zapisz
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* SMS Modal */}
      {smsModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <Card className="bg-gray-900 border-white/10 w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white">Wyślij SMS</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => { setSmsModal(null); setSmsMessage(''); }}>
                <X className="w-5 h-5" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-white/5 rounded-lg">
                <p className="text-gray-400 text-sm">Odbiorca</p>
                <p className="text-white font-medium">{smsModal.fullName || smsModal.name}</p>
                <p className="text-gray-500 text-sm">{smsModal.phone}</p>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">Wiadomość</Label>
                <textarea
                  value={smsMessage}
                  onChange={(e) => setSmsMessage(e.target.value)}
                  rows={4}
                  maxLength={160}
                  className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-white resize-none"
                  placeholder="Treść wiadomości SMS..."
                />
                <p className="text-gray-500 text-xs text-right">{smsMessage.length}/160</p>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                <Button variant="outline" onClick={() => { setSmsModal(null); setSmsMessage(''); }}>
                  Anuluj
                </Button>
                <Button 
                  onClick={handleSendSms} 
                  disabled={sendingSms}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {sendingSms ? 'Wysyłanie...' : 'Wyślij SMS'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminInstructors;
